import React, { useEffect, useRef, useState } from 'react';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { GTCInlineWrapper } from 'src/components/GenericTable';
import { useUpsertControlMutation } from 'src/graphql/_generated';

import { useAuth } from 'src/state/auth';
import styled from 'styled-components';
import Popper from '@mui/material/Popper';
import useOnClickOutside from 'src/hooks/useOnClickOutside';
import Button from 'src/components/form/Button';
import MaterialInput from 'src/components/form/MaterialInput';
import { InputLabel } from 'src/common';
import { OpenInNew } from '@mui/icons-material';
import * as Yup from 'yup';

const CellTrigger = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 14px;
  cursor: pointer;
  user-select: none;
  color: ${({ theme }) => theme.colors.prussianBlue};
  overflow: hidden;
  white-space: nowrap;
  min-width: 0;
`;

const CellLink = styled.a.attrs({ className: 'table-link' })`
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
`;

const CellText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledPopper = styled(Popper)`
  z-index: 2000;
`;

const PopperContainer = styled.div`
  box-shadow: 0px 0px 37px ${({ theme }) => theme.colors.shadow};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 6px;
  padding: 16px;
  min-width: 280px;
  display: grid;
  grid-gap: 12px;
`;

const urlSchema = Yup.string()
  .nullable()
  .test('url', 'Invalid URL', function (value) {
    if (!value || value.trim() === '') return true;
    return Yup.string().url().isValidSync(value);
  });

const textSchema = Yup.string().nullable().max(255, 'Max 255 characters');

interface LinkCellProps {
  controlId: string;
  linkText: string | null | undefined;
  linkUrl: string | null | undefined;
}

export const LinkCell: React.FC<LinkCellProps> = ({ controlId, linkText, linkUrl }) => {
  const [upsertControl, { loading }] = useUpsertControlMutation();
  const { workspaceId } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftText, setDraftText] = useState(linkText || '');
  const [draftUrl, setDraftUrl] = useState(linkUrl || '');
  const [textError, setTextError] = useState<string | undefined>();
  const [urlError, setUrlError] = useState<string | undefined>();
  const popperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const open = Boolean(anchorEl);

  useEffect(() => {
    setDraftText(linkText || '');
    setDraftUrl(linkUrl || '');
  }, [linkText, linkUrl]);

  useOnClickOutside(popperRef, (e) => {
    if (open && !triggerRef.current?.contains(e.target as Node)) {
      setAnchorEl(null);
    }
  });

  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(e.currentTarget);
      setTextError(undefined);
      setUrlError(undefined);
    }
  };

  const validateText = (val: string) => {
    try {
      textSchema.validateSync(val || null);
      setTextError(undefined);
      return true;
    } catch (err: any) {
      setTextError(err.message);
      return false;
    }
  };

  const validateUrl = (val: string) => {
    try {
      urlSchema.validateSync(val || null);
      setUrlError(undefined);
      return true;
    } catch (err: any) {
      setUrlError(err.message);
      return false;
    }
  };

  const validate = (): boolean => {
    const textValid = validateText(draftText);
    const urlValid = validateUrl(draftUrl);
    return textValid && urlValid;
  };

  const handleUpdate = () => {
    if (!validate()) return;

    const newText = draftText.trim() || null;
    const newUrl = draftUrl.trim() || null;

    if (newText === (linkText || null) && newUrl === (linkUrl || null)) {
      setAnchorEl(null);
      return;
    }

    upsertControl({
      variables: {
        controlData: { id: controlId, linkText: newText, linkUrl: newUrl },
        workspaceId,
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'Control', id: controlId }),
          fields: {
            linkText: () => newText,
            linkUrl: () => newUrl,
          },
        });
      },
      onCompleted: () => {
        queryCache.invalidateQueries([QKeys.Audit]);
        setAnchorEl(null);
      },
    });
  };

  const displayText = linkText || linkUrl;
  const hasValidUrl = Boolean(linkUrl && Yup.string().url().isValidSync(linkUrl));
  const isDirty = (draftText.trim() || null) !== (linkText || null) || (draftUrl.trim() || null) !== (linkUrl || null);
  const hasErrors = Boolean(textError || urlError);

  return (
    <GTCInlineWrapper>
      <CellTrigger ref={triggerRef} onClick={handleToggle}>
        {hasValidUrl ? (
          <CellLink
            href={linkUrl!}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <CellText>{displayText}</CellText>
            <OpenInNew css="font-size: 14px !important; color: inherit; flex-shrink: 0;" />
          </CellLink>
        ) : (
          <CellText>{displayText || '-'}</CellText>
        )}
      </CellTrigger>
      <StyledPopper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[
          { name: 'flip', enabled: true },
          { name: 'preventOverflow', options: { boundariesElement: 'scrollParent' } },
          { name: 'offset', options: { offset: [0, 4] } },
        ]}
      >
        <PopperContainer ref={popperRef}>
          <div>
            <InputLabel>LINK TEXT</InputLabel>
            <MaterialInput
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              onBlur={() => validateText(draftText)}
              placeholder="Enter link title"
              error={Boolean(textError)}
              helperText={textError}
              $small
              css="width: 100%;"
            />
          </div>
          <div>
            <InputLabel>LINK URL</InputLabel>
            <MaterialInput
              value={draftUrl}
              onChange={(e) => setDraftUrl(e.target.value)}
              onBlur={() => validateUrl(draftUrl)}
              placeholder="Enter URL"
              error={Boolean(urlError)}
              helperText={urlError}
              $small
              css="width: 100%;"
            />
          </div>
          <div>
            <Button primary $small onClick={handleUpdate} disabled={loading || !isDirty || hasErrors} loading={loading}>
              Update
            </Button>
          </div>
        </PopperContainer>
      </StyledPopper>
    </GTCInlineWrapper>
  );
};
